import React, { useEffect, useMemo, useState } from "react";
import { posAPI } from "../../services/api";
import { useI18n } from "../../i18n";
import { useDeliveryLines } from "../../hooks/useDeliveryLines";
import { PRODUCT_CATEGORIES } from "../../constants/categories";
import { isPackItem } from "../utils/packs";
import { sanitizeAmount } from "../utils/amountEntry";
import Sheet from "./Sheet";

const input =
  "w-full h-11 px-3 rounded-xl bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-primary-400";

/**
 * A supplier stops by while only the cashier is in.
 *
 * Two things can happen, both from here:
 *  - **Delivery** — what came in, how many, and what each lot cost if the
 *    paper says; prices can be left for the owner. Stock goes up at once. A
 *    product the shop does not stock yet goes in as a draft. Anything paid
 *    there and then comes out of the drawer.
 *  - **Pay only** — money out of the drawer to a supplier, the delivery to be
 *    written up by the owner later.
 *
 * Never shows what things cost the shop or what the supplier is owed: the
 * register answers with what was counted in, nothing more.
 */
const SupplierSheet = ({ open, onClose, products, currency, canReceive, canPay, startIn, onDone }) => {
  const { t } = useI18n();
  const [mode, setMode] = useState(canReceive ? "delivery" : "payout");
  const [suppliers, setSuppliers] = useState([]);
  const [supplierName, setSupplierName] = useState("");
  const [search, setSearch] = useState("");
  const [newProduct, setNewProduct] = useState(null);
  const [paidNow, setPaidNow] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const delivery = useDeliveryLines();

  useEffect(() => {
    if (!open) return;
    // Mounted across cashiers: start from what this one may do
    setMode(startIn === "payout" && canPay ? "payout" : canReceive ? "delivery" : "payout");
    posAPI
      .suppliers()
      .then(({ data }) => setSuppliers(data))
      .catch(() => setSuppliers([]));
  }, [open, canReceive, canPay, startIn]);

  const known = suppliers.find(
    (supplier) => supplier.name.toLowerCase() === supplierName.trim().toLowerCase(),
  );

  const matches = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return [];
    const chosen = new Set(delivery.lines.map((line) => line.product_id));
    return products
      .filter((product) => !isPackItem(product) && !chosen.has(product.id))
      .filter((product) => `${product.name} ${product.brand || ""}`.toLowerCase().includes(term))
      .slice(0, 6);
  }, [search, products, delivery.lines]);

  const reset = () => {
    delivery.reset();
    setSupplierName("");
    setSearch("");
    setPaidNow("");
    setNote("");
    setNewProduct(null);
    setError("");
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    setBusy(true);
    setError("");

    try {
      if (mode === "delivery") {
        await posAPI.receiveDelivery({
          ...(known ? { supplier_id: known.id } : { supplier_name: supplierName.trim() || undefined }),
          items: delivery.toItems(),
          paid_now: paidNow !== "" ? parseFloat(paidNow) : undefined,
          paid_from: "drawer",
          notes: note || undefined,
        });
        onDone(t("pos.supplier.received"));
      } else {
        await posAPI.payoutSupplier(known.id, parseFloat(paidNow), note || undefined);
        onDone(t("pos.supplier.paid", { name: known.name }));
      }
      close();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || t("pos.supplier.failed"));
    } finally {
      setBusy(false);
    }
  };

  const ready =
    mode === "delivery"
      ? delivery.summary.complete && (paidNow === "" || supplierName.trim())
      : Boolean(known) && parseFloat(paidNow) > 0;

  const tab = (value, label) => (
    <button
      type="button"
      onClick={() => setMode(value)}
      aria-pressed={mode === value}
      className={`flex-1 h-10 rounded-lg text-sm font-semibold ${
        mode === value ? "bg-primary-500 text-white" : "bg-slate-800 text-slate-300"
      }`}
    >
      {label}
    </button>
  );

  return (
    <Sheet
      open={open}
      onClose={close}
      title={t("pos.supplier.title")}
      subtitle={t("pos.supplier.subtitle")}
      footer={
        <button
          type="button"
          disabled={!ready || busy}
          onClick={submit}
          className="w-full h-14 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-white text-lg font-bold disabled:opacity-40"
        >
          {busy
            ? t("common.saving")
            : mode === "delivery"
              ? t("pos.supplier.recordDelivery")
              : t("pos.supplier.recordPayout")}
        </button>
      }
    >
      <div className="p-4 space-y-4">
        {canReceive && canPay && (
          <div className="flex gap-2">
            {tab("delivery", t("pos.supplier.delivery"))}
            {tab("payout", t("pos.supplier.payOnly"))}
          </div>
        )}

        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            {t("pos.supplier.who")}
          </span>
          <input
            type="text"
            list="pos-suppliers"
            autoComplete="off"
            value={supplierName}
            onChange={(e) => setSupplierName(e.target.value)}
            placeholder={t("pos.supplier.whoPlaceholder")}
            className={`mt-1 ${input}`}
          />
          <datalist id="pos-suppliers">
            {suppliers.map((supplier) => (
              <option key={supplier.id} value={supplier.name} />
            ))}
          </datalist>
          {mode === "payout" && supplierName.trim() && !known && (
            <span className="mt-1 block text-xs text-amber-300">{t("pos.supplier.unknown")}</span>
          )}
        </label>

        {mode === "delivery" && (
          <>
            <div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("pos.supplier.searchProduct")}
                  className={`flex-1 min-w-0 ${input}`}
                />
                <button
                  type="button"
                  onClick={() => setNewProduct({ name: search.trim(), category: "juice" })}
                  className="shrink-0 h-11 px-3 rounded-xl bg-slate-800 border border-slate-700 text-sm font-semibold text-slate-200"
                >
                  ＋ {t("pos.supplier.new")}
                </button>
              </div>
              {matches.length > 0 && (
                <ul className="mt-1 rounded-xl border border-slate-700 divide-y divide-slate-800 overflow-hidden">
                  {matches.map((product) => (
                    <li key={product.id}>
                      <button
                        type="button"
                        onClick={() => {
                          delivery.addProduct(product);
                          setSearch("");
                        }}
                        className="w-full text-start px-3 py-2.5 hover:bg-slate-800 text-sm text-white"
                      >
                        {product.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {newProduct && (
                <div className="mt-2 space-y-2 rounded-xl border border-dashed border-slate-600 p-3">
                  <input
                    type="text"
                    autoFocus
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                    placeholder={t("finance.deliveries.newProductName")}
                    className={input}
                  />
                  <div className="flex gap-2">
                    <select
                      value={newProduct.category}
                      onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                      className={`flex-1 ${input}`}
                    >
                      {PRODUCT_CATEGORIES.map((category) => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      disabled={!newProduct.name.trim()}
                      onClick={() => {
                        delivery.addNewProduct(newProduct.name, newProduct.category);
                        setNewProduct(null);
                        setSearch("");
                      }}
                      className="h-11 px-4 rounded-xl bg-primary-500 text-white font-semibold disabled:opacity-40"
                    >
                      {t("finance.deliveries.addLine")}
                    </button>
                  </div>
                  <p className="text-xs text-slate-400">{t("finance.deliveries.newProductHint")}</p>
                </div>
              )}
            </div>

            {delivery.lines.length > 0 && (
              <ul className="space-y-2">
                {delivery.lines.map((line) => (
                  <li key={line.key} className="rounded-xl bg-slate-800/60 border border-slate-700 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-white">
                        {line.name}
                        {line.new_product && (
                          <span className="ms-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">
                            {t("finance.deliveries.newBadge")}
                          </span>
                        )}
                      </p>
                      <button
                        type="button"
                        onClick={() => delivery.removeLine(line.key)}
                        aria-label={t("finance.deliveries.removeLine")}
                        className="text-slate-400 hover:text-red-400"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <label className="text-xs text-slate-400">
                        {t("finance.deliveries.columns.quantity")}
                        <input
                          type="text"
                          inputMode="numeric"
                          dir="ltr"
                          value={line.quantity}
                          onChange={(e) =>
                            delivery.setLine(line.key, { quantity: e.target.value.replace(/\D/g, "") })
                          }
                          className={`mt-1 ${input} text-end`}
                        />
                      </label>
                      <label className="text-xs text-slate-400">
                        {t("pos.supplier.lotTotal", { currency })}
                        <input
                          type="text"
                          inputMode="decimal"
                          dir="ltr"
                          placeholder={t("finance.deliveries.later")}
                          value={line.line_total}
                          onChange={(e) =>
                            delivery.setLine(line.key, { line_total: sanitizeAmount(e.target.value) })
                          }
                          className={`mt-1 ${input} text-end`}
                        />
                      </label>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        {(mode === "payout" || canPay) && (
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              {mode === "payout"
                ? t("pos.supplier.amount", { currency })
                : t("pos.supplier.paidFromDrawer", { currency })}
            </span>
            <input
              type="text"
              inputMode="decimal"
              dir="ltr"
              placeholder="0"
              value={paidNow}
              onChange={(e) => setPaidNow(sanitizeAmount(e.target.value))}
              className={`mt-1 ${input} text-end text-xl font-bold`}
            />
            {mode === "delivery" && (
              <span className="mt-1 block text-xs text-slate-400">{t("pos.supplier.paidHint")}</span>
            )}
          </label>
        )}

        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            {t("pos.supplier.note")}
          </span>
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)} className={`mt-1 ${input}`} />
        </label>

        {error && <p className="text-sm font-semibold text-red-300">{error}</p>}
      </div>
    </Sheet>
  );
};

export default SupplierSheet;
